import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { DateService, LogService, UserService } from '@oa/shared-core';

import { SpatialService} from '@app/services/spatial.service';


import * as turf from '@turf/turf';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';


@Injectable({
  providedIn: "root",
})
export class FieldDataCollectionService {
  private readonly _componentName = 'FieldDataCollectionService';
  private _defaultBufferDistance = 800; //units are feet

  /**
   * Each field data collection feature has a property called "datasetName". oa-field-data   *
   */
  public fieldDataDatasetName = 'oa-field-data';

  /**
   * Field Data
   */
  public fieldData = new BehaviorSubject<any>(null);

  /**
   * The buffer distance to use when filter field data by the listing boundary feature
   */
  public get defaultBufferDistance(): number {
    return this._defaultBufferDistance;
  }

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private log: LogService,
    private dateService: DateService,
    private spatialService: SpatialService
  ) {}

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    // return true;
    return this.log.verboseLogging;
  }

  /**
   * Gets the ArcGIS Field Data Collection
   */
  public getFieldDataCollection(forceRefresh: boolean): Observable<any> {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.getFieldDataCollection forceRefresh=`, forceRefresh);
    }

    let url = 'listing/agol/field-data';

    if (forceRefresh) {
      url = `${url}/true`;
    }

    return this.http.get<any>(url).pipe(
      map((res) => {
        // console.log('AppSettings', res.json());

        this.fieldData.next(res);
        return res;
      })
    );
  }

  /**
   * Get attachments for a feature
   * @param feature
   */
  public getAttachments(fieldData: any, feature: any) {
    console.log(`${this._componentName}.getAttachments(fieldData,feature) feature=`, feature);

    if (!fieldData) {
      fieldData = this.fieldData.value;
    }

    const globalId = feature.getProperty('globalID');

    return this.getAttachmentsByGlobalId(fieldData, globalId);
  }

  public getAttachmentsByGlobalId(fieldData: any, globalId: string) {
    // console.log(`${this._componentName}.getAttachmentsByGlobalId globalId=`, globalId);

    if (!fieldData) {
      fieldData = this.fieldData.value;
    }

    let attachments = [];

    if (fieldData) {
      fieldData.pointAttachments.attachmentGroups.filter((attachmentGroup) => {
        if (attachmentGroup.parentGlobalId === globalId) {
          attachmentGroup.attachmentInfos.forEach((info) => {
            attachments.push(info);
          });
        }
      });

      // console.log(`${this._componentName}.getAttachmentsByGlobalId attachments=`, attachments);
    }

    return attachments;
  }

  /**
   * Filters field data by an enevelope extent
   * @param fieldData
   * @param lng1
   * @param lat1
   * @param lng2
   * @param lat2
   */
  public filter(fieldData: any, lng1: number, lat1: number, lng2: number, lat2: number): any {
    console.log(`${this._componentName}.filter lng1=${lng1},lat1=${lat1},lng2=${lng2},lat2=${lat2} `);

    let features = turf.featureCollection([
      turf.point([lng1, lat1], { name: 'Position 1' }),
      turf.point([lng2, lat2], { name: 'Position 2' }),
    ]);

    let envelope = turf.envelope(features);

    console.log(`${this._componentName}.filter envelope=`, envelope);

    const filteredPoints = fieldData.pointFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanPointInPolygon
      if (turf.booleanPointInPolygon(f.geometry, envelope)) {
        return f;
      }
    });

    const filteredPolygons = fieldData.polyFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanDisjoint
      if (!turf.booleanDisjoint(f.geometry, envelope)) {
        return f;
      }
    });

    const filteredLines = fieldData.lineFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanDisjoint

      const featureEnvelope = turf.envelope(f);

      if (!turf.booleanDisjoint(featureEnvelope, envelope)) {
        return f;
      }
    });

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filtered points=`, filteredPoints);
      console.log(`${this._componentName}.filtered polygons=`, filteredPolygons);
      console.log(`${this._componentName}.filtered lines=`, filteredLines);
    }

    let returnItem = { ...fieldData };

    returnItem.polyFeatureCollection.features = filteredPolygons;
    returnItem.lineFeatureCollection.features = filteredLines;
    returnItem.pointFeatureCollection.features = filteredPoints;

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filtered point count=${filteredPoints.length}`);
      console.log(`${this._componentName}.filtered line count=${filteredLines.length}`);
      console.log(`${this._componentName}.filtered polygon count=${filteredPolygons.length}`);
    }

    return returnItem;
  }

  /**
   * Filters field data by boundary feature geometries
   * @param fieldData
   * @param polygon
   */
  public filterByBoundaryFeatures(fieldData: any, boundaryFeatures: [], bufferDistance: number): any {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByBoundaryFeatures -  boundaryFeatures=`, boundaryFeatures);
    }

    if (!fieldData) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.filterByBoundaryFeatures - fieldData is null/empty. returning null.`);
      }
      return null;
    }

    if (!boundaryFeatures || (boundaryFeatures && boundaryFeatures.length === 0)) {
      console.log(`${this._componentName}.filterByBoundaryFeatures - boundaryFeatures is null/empty. returning null.`);
      return null;
    }

    if (bufferDistance === undefined || bufferDistance === null) {
      bufferDistance = this.defaultBufferDistance;
      if (this.verboseLogging) {
        console.log(
          `${this._componentName}.filterByBoundaryFeatures - no buffer distance provided. Using default buffer distance of: ${this.defaultBufferDistance} feet.`
        );
      }
    } else {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.filterByBoundaryFeatures - ${bufferDistance} provided.`);
      }
    }

    let bufferOptions: any = { units: 'feet' };

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByBoundaryFeatures - bufferOptions:`, bufferOptions);
      console.log(`${this._componentName}.filterByBoundaryFeatures - bufferDistance:`, bufferDistance);
    }

    const bufferFeatures = boundaryFeatures.map((f) => {
      return turf.buffer(f, bufferDistance, bufferOptions);
    });

    // union -  https://turfjs.org/docs/#union
    let filterPolygon: any = null;

    if (bufferFeatures.length === 1) {
      filterPolygon = bufferFeatures[0].geometry;
    } else if (bufferFeatures.length > 0) {
      filterPolygon = this.spatialService.multiUnion(bufferFeatures);
      // filterPolygon = turf.union.apply(bufferFeatures);
    }

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByBoundaryFeatures - filterPolygon=`, JSON.stringify(filterPolygon, null, 4));
    }

    const filteredPoints = fieldData.pointFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanPointInPolygon
      if (turf.booleanPointInPolygon(f.geometry, filterPolygon)) {
        return f;
      }
    });

    const filteredPolygons = fieldData.polyFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanDisjoint
      if (!turf.booleanDisjoint(f.geometry, filterPolygon)) {
        return f;
      }
    });

    const filteredLines = fieldData.lineFeatureCollection.features.filter((f) => {
      // https://turfjs.org/docs/#booleanDisjoint

      const featureEnvelope = turf.envelope(f);

      if (!turf.booleanDisjoint(featureEnvelope, filterPolygon)) {
        return f;
      }
    });

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByBoundaryFeatures points=`, filteredPoints);
      console.log(`${this._componentName}.filterByBoundaryFeatures polygons=`, filteredPolygons);
      console.log(`${this._componentName}.filterByBoundaryFeatures lines=`, filteredLines);
    }

    let returnItem = { ...fieldData };

    returnItem.polyFeatureCollection.features = filteredPolygons;
    returnItem.lineFeatureCollection.features = filteredLines;
    returnItem.pointFeatureCollection.features = filteredPoints;

    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByBoundaryFeatures point count=${filteredPoints.length}`);
      console.log(`${this._componentName}.filterByBoundaryFeatures line count=${filteredLines.length}`);
      console.log(`${this._componentName}.filterByBoundaryFeatures polygon count=${filteredPolygons.length}`);
    }

    return returnItem;
  }

  /**
   * Filters a feature collection by a collection of type names
   * @param featureCollection
   * @param typeNames
   */
  public filterByTypeName(originalFeatureCollection: any, typeNames: string[]) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.filterByTypeName typeNames=`, typeNames);
    }

    let featureCollection = { ...originalFeatureCollection };

    const originalFeatureCount = featureCollection.features.length;

    const filteredFeatures = featureCollection.features.filter((f) => {
      let featureTypeName = f.properties.pointType || f.properties.lineType;

      if (featureTypeName) {
        featureTypeName = (featureTypeName + '').toLowerCase();
      }

      if (f && featureTypeName) {
        if (typeNames.includes(featureTypeName)) {
          return f;
        }
      } else {
        console.warn(`${this._componentName} features have no pointType or lineType: `, f);
      }
    });

    const filteredFeatureCount = filteredFeatures.length;

    featureCollection.features = filteredFeatures;

    if (this.verboseLogging) {
      console.log(
        `${this._componentName}.filterByTypeName  Original Feature Count: ${originalFeatureCount}, Filtered Feature Count: ${filteredFeatureCount}.  typeNames=`,
        typeNames
      );
    }
    return featureCollection;
  }
}
